export interface SsbdHttp503FailedRequest {
  url: string;
  method?: string;
}

interface MicroLabelRule {
  pattern: RegExp;
  es: string;
  en: string;
}

const MICRO_LABEL_RULES: MicroLabelRule[] = [
  { pattern: /kiosk\/pnr|mytrips\/reservation/i, es: 'Lookup PNR', en: 'PNR lookup' },
  { pattern: /getPassengerData/i, es: 'Datos pasajero SSBD', en: 'SSBD passenger data' },
  { pattern: /bagtag\/eligibility/i, es: 'Elegibilidad bag tag', en: 'Bag tag eligibility' },
  { pattern: /\/rc\/bagtag|\/checkin\/bagtag/i, es: 'Emision bag tag', en: 'Bag tag issue' },
  { pattern: /ancillar/i, es: 'Ancillaries', en: 'Ancillaries' },
  { pattern: /pytsbk|\/payment/i, es: 'Pago', en: 'Payment' },
  { pattern: /docheckin/i, es: 'Check-in', en: 'Check-in' },
  { pattern: /boardingPass/i, es: 'Boarding pass', en: 'Boarding pass' },
  { pattern: /getViewDocuments/i, es: 'Documentos pasajero', en: 'Passenger documents' },
  { pattern: /eligibility/i, es: 'Elegibilidad check-in', en: 'Check-in eligibility' },
  { pattern: /kiosk\/profile|checkin\/profile/i, es: 'Perfil kiosko', en: 'Kiosk profile' },
  { pattern: /flightstats|flightstatus/i, es: 'FlightStats', en: 'FlightStats' },
  { pattern: /flightDetails/i, es: 'Detalle de vuelo', en: 'Flight details' },
  { pattern: /selfreaccom|\/sc\//i, es: 'IROPS / reacomodo', en: 'IROPS / reaccommodation' },
  { pattern: /loyal/i, es: 'Lealtad', en: 'Loyalty' },
  { pattern: /contactability/i, es: 'Contactabilidad', en: 'Contactability' },
];

const PECTAB_LINE_WIDTH = 68;

function padPectabLine(text: string): string {
  const clipped = text.slice(0, PECTAB_LINE_WIDTH);
  return ('    ' + clipped).padEnd(4 + PECTAB_LINE_WIDTH, ' ');
}

export function extractEndpointPath(url: string): string {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(url);
    return parsed.pathname.replace(/^\/+/, '');
  } catch {
    return url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+\/?/i, '').replace(/[?#].*$/, '');
  }
}

function resolveMicroLabel(url: string, lang: string): string {
  const rule = MICRO_LABEL_RULES.find((entry) => entry.pattern.test(url));
  if (!rule) {
    return lang === 'es_MX' ? 'Microservicio de negocio' : 'Business microservice';
  }
  return lang === 'es_MX' ? rule.es : rule.en;
}

export function buildSsbd503PrintLines(
  failedRequest: SsbdHttp503FailedRequest | null | undefined,
  lang: string
): { header4: string; messageLong: string } {
  const url = failedRequest?.url ?? '';
  const endpoint = extractEndpointPath(url);
  const microLabel = resolveMicroLabel(url, lang);
  const method = failedRequest?.method?.toUpperCase() ?? 'HTTP';

  const affectedLabel = lang === 'es_MX' ? 'Servicio afectado: ' : 'Affected service: ';
  const technicalLabel = lang === 'es_MX' ? 'Referencia técnica: ' : 'Technical reference: ';

  const affectedValue = endpoint
    ? microLabel
    : SSBD_FALLBACK_AFFECTED[lang === 'es_MX' ? 'es' : 'en'];

  const technicalPrefix = endpoint
    ? `${technicalLabel}ERR-503-SSBD | ${method} `
    : `${technicalLabel}ERR-503-SSBD | endpoint no disponible`;
  const technicalLine = endpoint
    ? technicalPrefix + truncateKeepingTail(endpoint, PECTAB_LINE_WIDTH - technicalPrefix.length)
    : technicalPrefix;

  return {
    header4: padPectabLine(affectedLabel + affectedValue),
    messageLong: padPectabLine(technicalLine),
  };
}

const SSBD_FALLBACK_AFFECTED = {
  es: 'Microservicio SSBD (endpoint no identificado)',
  en: 'SSBD microservice (unidentified endpoint)',
};

function truncateKeepingTail(endpoint: string, max: number): string {
  if (max <= 0) {
    return '';
  }
  if (endpoint.length <= max) {
    return endpoint;
  }
  if (max <= 3) {
    return endpoint.slice(-max);
  }
  return '...' + endpoint.slice(-(max - 3));
}
